import { useEffect, useState } from "react";
import { apiFetch } from "../config/api";
import { getUser } from "../config/auth";
import { canManageSettings } from "../config/rbac";
import Loader from "../components/Loader";
import PageHeader from "../components/PageHeader";
import Toast, { useToast } from "../components/Toast";

export default function SettingsPage() {
  const me = getUser();
  const isAdmin = canManageSettings(me?.role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { toast, showToast, dismissToast } = useToast();

  const [orgName, setOrgName] = useState("");
  const [remindersEnabled, setRemindersEnabled] = useState(true);

  const [webhookUrl, setWebhookUrl] = useState(window.location.origin);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookMsg, setWebhookMsg] = useState({ text: "", ok: true });
  const [alertAfterDays, setAlertAfterDays] = useState(1);
  const [frequencyDays, setFrequencyDays] = useState(1);
  const [senderName, setSenderName] = useState("");
  const [alertAfterDaysMsg, setAlertAfterDaysMsg] = useState("");
  const [frequencyDaysMsg, setFrequencyDaysMsg] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const res = await apiFetch("/api/settings");
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        setOrgName(data.orgName || "");
        setRemindersEnabled(data.overdueRemindersEnabled ?? true);
        setAlertAfterDays(data.overdueAlertAfterDays ?? 1);
        setFrequencyDays(data.reminderFrequencyDays ?? 1);
        setSenderName(data.reminderSenderName || "");
      } catch (e) {
        setError(e?.message || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function registerWebhook() {
    setWebhookSaving(true);
    setWebhookMsg({ text: "", ok: true });
    try {
      const res = await apiFetch("/api/telegram/register-webhook", {
        method: "POST",
        body: JSON.stringify({ webhookUrl }),
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text);
      setWebhookMsg({ text: "✅ Webhook registered! Telegram notifications are now active.", ok: true });
    } catch (e) {
      setWebhookMsg({ text: e?.message || "Failed to register webhook", ok: false });
    } finally {
      setWebhookSaving(false);
    }
  }

  async function save(e) {
    e.preventDefault();
    if (!isAdmin) return;
    setError("");

    if (remindersEnabled) {
      if (!Number.isInteger(Number(alertAfterDays)) || Number(alertAfterDays) < 1) {
        return setError("\"Alert after\" must be a whole number of at least 1 day.");
      }
      if (!Number.isInteger(Number(frequencyDays)) || Number(frequencyDays) < 1) {
        return setError("\"Re-send every\" must be a whole number of at least 1 day.");
      }
    }

    setSaving(true);
    try {
      const res = await apiFetch("/api/settings", {
        method: "PUT",
        body: JSON.stringify({
          orgName,
          overdueRemindersEnabled: remindersEnabled,
          overdueAlertAfterDays: Number(alertAfterDays),
          reminderFrequencyDays: Number(frequencyDays),
          reminderSenderName: senderName || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      showToast("Settings saved successfully.");
    } catch (e) {
      showToast(e?.message || "Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="System-wide configuration — visible to all, editable by Admin only"
      />

      {loading ? (
        <div className="glass-card" style={{ padding: 32, display: "flex", justifyContent: "center" }}>
          <Loader />
        </div>
      ) : (
        <form onSubmit={save}>
          {/* Organisation */}
          <div className="glass-card" style={{ padding: 28, marginBottom: 20 }}>
            <SectionHeader
              title="Organisation"
              desc="Basic details shown across the platform"
            />
            <Field label="Organisation Name">
              <input
                className="form-input"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                disabled={!isAdmin}
                placeholder="e.g. Tackl"
              />
            </Field>
          </div>

          {/* Overdue Reminders */}
          <div className="glass-card" style={{ padding: 28, marginBottom: 20 }}>
            <SectionHeader
              title="Overdue Task Reminders"
              desc="Automated email reminders for tasks that are past their due date"
            />

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: isAdmin ? "pointer" : "default" }}>
                <div
                  onClick={() => isAdmin && setRemindersEnabled((v) => !v)}
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    background: remindersEnabled ? "var(--brand, #0891b2)" : "#d1d5db",
                    position: "relative",
                    transition: "background 0.2s",
                    cursor: isAdmin ? "pointer" : "default",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: "#fff",
                      position: "absolute",
                      top: 3,
                      left: remindersEnabled ? 23 : 3,
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
                <span style={{ fontWeight: 600, fontSize: 14, color: "var(--text-1)" }}>
                  Enable overdue reminders
                </span>
              </label>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 16,
                opacity: remindersEnabled ? 1 : 0.45,
                pointerEvents: remindersEnabled ? "auto" : "none",
              }}
            >
              <div>
                <Field label="Alert after (days overdue)">
                  <input
                    className="form-input"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    value={alertAfterDays}
                    onChange={(e) => { setAlertAfterDays(e.target.value); setAlertAfterDaysMsg(""); }}
                    onBlur={(e) => { const v = parseInt(e.target.value, 10); if (isNaN(v) || v < 1) { setAlertAfterDaysMsg("Must be at least 1. Reset to 1."); setAlertAfterDays(1); } }}
                    disabled={!isAdmin}
                  />
                </Field>
                {alertAfterDaysMsg && <div style={{ fontSize: 12, color: "var(--warn-text)", marginTop: 4 }}>{alertAfterDaysMsg}</div>}
              </div>
              <div>
                <Field label="Re-send every (days)">
                  <input
                    className="form-input"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    value={frequencyDays}
                    onChange={(e) => { setFrequencyDays(e.target.value); setFrequencyDaysMsg(""); }}
                    onBlur={(e) => { const v = parseInt(e.target.value, 10); if (isNaN(v) || v < 1) { setFrequencyDaysMsg("Must be at least 1. Reset to 1."); setFrequencyDays(1); } }}
                    disabled={!isAdmin}
                  />
                </Field>
                {frequencyDaysMsg && <div style={{ fontSize: 12, color: "var(--warn-text)", marginTop: 4 }}>{frequencyDaysMsg}</div>}
              </div>
              <Field label="Sender name (optional)">
                <input
                  className="form-input"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  disabled={!isAdmin}
                  placeholder="e.g. Tackl"
                />
              </Field>
            </div>
          </div>

          {/* Telegram Bot */}
          {isAdmin && (
            <div className="glass-card" style={{ padding: 28, marginBottom: 20 }}>
              <SectionHeader
                title="Telegram Bot"
                desc="Register the webhook so the bot can receive messages and send notifications"
              />
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <Field label="Backend URL (where this API is hosted)">
                    <input
                      className="form-input"
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder="https://your-api.example.com"
                    />
                  </Field>
                </div>
                <button
                  type="button"
                  className="btn"
                  onClick={registerWebhook}
                  disabled={webhookSaving || !webhookUrl.trim()}
                  style={{ padding: "10px 20px", borderRadius: 12, fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  {webhookSaving ? "Registering…" : "Register Webhook"}
                </button>
              </div>
              {webhookMsg.text && (
                <div style={{
                  marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: 13,
                  background: webhookMsg.ok ? "#f0fdf4" : "var(--danger-soft)",
                  border: `1px solid ${webhookMsg.ok ? "#86efac" : "var(--danger-border)"}`,
                  color: webhookMsg.ok ? "#166534" : "var(--danger-text)",
                }}>
                  {webhookMsg.text}
                </div>
              )}
              <div style={{ marginTop: 12, fontSize: 12, color: "var(--text-3)" }}>
                Bot: <strong>@TacklWorkflowBot</strong> — Users link via Profile → Connect Telegram
              </div>
            </div>
          )}

          {/* Validation errors shown inline */}
          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid var(--danger-border)",
                background: "var(--danger-soft)",
                color: "var(--danger-text)",
                marginBottom: 16,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {isAdmin && (
            <button
              type="submit"
              className="btn"
              disabled={saving}
              style={{ padding: "10px 24px", borderRadius: 12, fontWeight: 600 }}
            >
              {saving ? "Saving…" : "Save Settings"}
            </button>
          )}
          {!isAdmin && (
            <p style={{ fontSize: 13, color: "var(--text-2)" }}>
              You have read-only access. Contact an Admin to change settings.
            </p>
          )}
        </form>
      )}
      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
}

function SectionHeader({ title, desc }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-1)" }}>{title}</div>
      <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>{desc}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}
